import { useState, useMemo } from 'react';
import EspacioCard from '../components/EspacioCard';
import { useLanguage } from '../contexts/LanguageContext';

/*
 * ===================== LOCALES =====================
 * Para añadir un nuevo local:
 * 1. Copia un bloque { id, src, alt, overlayText, mapsUrl, location, detailSlug, features } de abajo.
 * 2. Cambia el "id" por el siguiente número disponible.
 * 3. "src": ruta de la imagen (en /public o URL externa).
 * 4. "alt": texto descriptivo de la imagen para accesibilidad.
 * 5. "overlayText": dirección completa que se muestra sobre la imagen.
 * 6. "mapsUrl": enlace de Google Maps. Usa el formato:
 *    https://www.google.com/maps/search/?api=1&query=DIRECCIÓN+CON+PLUS
 * 7. "location": nombre corto del local (ej. "C/ Caballero de Rodas, 43").
 *    Este campo se usa para agrupar imágenes en el filtro desplegable.
 *    Si añades un local nuevo con un "location" diferente, aparecerá
 *    automáticamente como nueva opción en el desplegable.
 *    El filtro solo se muestra si hay más de 1 ubicación.
 * 8. "detailSlug": identificador único para la URL de detalle (ej. "caballero-rodas-43").
 *    Todas las imágenes del mismo local deben tener el MISMO detailSlug.
 *    La página de detalle se accede en /locales/[detailSlug].
 * 9. "features": array de características del local. Solo hace falta ponerlo
 *    en la PRIMERA imagen de cada local (las demás del mismo slug lo heredan).
 */
const LOCALES_IMAGES = [
  // --- Local en C/ Example, 10 ---
  {
    id: 1,
    src: 'https://picsum.photos/seed/loc1/800/500',
    alt: 'Local comercial Alicante',
    overlayText: 'C/ Caballero de Rodas, 43, 03181 Torrevieja, Alicante',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=C.+Caballero+de+Rodas+43+03181+Torrevieja+Alicante',
    location: 'C/ Example, 10',
    detailSlug: 'example-10',
    features: ['120 m²', 'Aire acondicionado', 'Escaparate', 'Zona céntrica', 'Acceso 24h'],
  },
  // --- Local en C/ Caballero de Rodas, 43 ---
  {
    id: 2,
    src: 'https://picsum.photos/seed/loc2/800/500',
    alt: 'Local Torrevieja',
    overlayText: 'C/ Caballero de Rodas, 43, 03181 Torrevieja, Alicante',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=C.+Caballero+de+Rodas+43+03181+Torrevieja+Alicante',
    location: 'C/ Caballero de Rodas, 43',
    detailSlug: 'caballero-rodas-43',
    features: ['85 m²', 'Reformado', 'Aire acondicionado', 'Zona peatonal', 'Almacén'],
  },
  // --- Local en C/ Ejemplo, 4 ---
  {
    id: 3,
    src: 'https://picsum.photos/seed/loc3/800/500',
    alt: 'Espacio versátil',
    overlayText: 'C/ Ejemplo, 4, 03181 Torrevieja, Alicante',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=C/+Ejemplo+4+03181+Torrevieja+Alicante',
    location: 'C/ Ejemplo, 4',
    detailSlug: 'ejemplo-4',
    features: ['200 m²', 'Dos plantas', 'Parking privado', 'Aire acondicionado', 'Diáfano'],
  },
  {
    id: 4,
    src: 'https://picsum.photos/seed/loc4/800/500',
    alt: 'Local en zona céntrica',
    overlayText: 'C/ Ejemplo, 4, 03181 Torrevieja, Alicante',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=C/+Ejemplo+4+03181+Torrevieja+Alicante',
    location: 'C/ Ejemplo, 4',
    detailSlug: 'ejemplo-4',
  },
];

/*
 * ===================== OFICINAS COWORKING =====================
 * Para añadir una nueva oficina de coworking:
 * 1. Copia un bloque { id, src, alt, overlayText, mapsUrl, location, detailSlug, features } de abajo.
 * 2. Cambia el "id" por el siguiente número disponible (actualmente el último es 11).
 * 3. "src": ruta de la imagen (en /public o URL externa).
 * 4. "alt": texto descriptivo de la imagen para accesibilidad.
 * 5. "overlayText": dirección completa que se muestra sobre la imagen.
 *    Al hacer clic se abre Google Maps con esa dirección.
 * 6. "mapsUrl": enlace de Google Maps. Usa el formato:
 *    https://www.google.com/maps/search/?api=1&query=DIRECCIÓN+CON+PLUS
 * 7. "location": nombre corto de la oficina (ej. "C/ Caballero de Rodas, 43").
 *    Este campo se usa para agrupar imágenes en el filtro desplegable.
 *    Si añades una oficina con un "location" diferente, aparecerá
 *    automáticamente como nueva opción en el desplegable.
 *    El filtro solo se muestra si hay más de 1 ubicación.
 * 8. "detailSlug": identificador único para la URL de detalle (ej. "coworking-caballero-43").
 *    Todas las imágenes de la misma oficina deben tener el MISMO detailSlug.
 *    La página de detalle se accede en /locales/[detailSlug].
 * 9. "features": array de características de la oficina. Solo hace falta ponerlo
 *    en la PRIMERA imagen de cada oficina (las demás del mismo slug lo heredan).
 */
const COWORKING_IMAGES = [
  // --- Oficinas en C/ Caballero de Rodas, 43 ---
  {
    id: 1,
    src: 'https://res.cloudinary.com/dgggdig3s/image/upload/f_auto,q_auto/fondo-hero_ufghpb.jpg',
    alt: 'Oficina coworking en C/ Caballero de Rodas, 43',
    overlayText: 'C/ Caballero de Rodas, 43, 03181 Torrevieja, Alicante',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=C.+Caballero+de+Rodas+43+03181+Torrevieja+Alicante',
    location: 'C/ Caballero de Rodas, 43',
    detailSlug: 'coworking-caballero-43',
    features: ['WiFi de alta velocidad', 'Sala de reuniones', 'Aire acondicionado', 'Recepción', 'Cocina compartida', 'Impresora', 'Acceso 24h', 'Limpieza incluida'],
  },
  {
    id: 2,
    src: 'https://res.cloudinary.com/dgggdig3s/image/upload/f_auto,q_auto/AGAMED_43-1-1_klzmyk.jpg',
    alt: 'Oficina coworking en C/ Caballero de Rodas, 43',
    overlayText: 'C/ Caballero de Rodas, 43, 03181 Torrevieja, Alicante',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=C.+Caballero+de+Rodas+43+03181+Torrevieja+Alicante',
    location: 'C/ Caballero de Rodas, 43',
    detailSlug: 'coworking-caballero-43',
  },
  {
    id: 3,
    src: 'https://res.cloudinary.com/dgggdig3s/image/upload/f_auto,q_auto/AGAMED_43-1-2_vmwutz.jpg',
    alt: 'Oficina coworking en C/ Caballero de Rodas, 43',
    overlayText: 'C/ Caballero de Rodas, 43, 03181 Torrevieja, Alicante',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=C.+Caballero+de+Rodas+43+03181+Torrevieja+Alicante',
    location: 'C/ Caballero de Rodas, 43',
    detailSlug: 'coworking-caballero-43',
  },
  {
    id: 4,
    src: 'https://res.cloudinary.com/dgggdig3s/image/upload/f_auto,q_auto/AGAMED_43-1-3_klkejo.jpg',
    alt: 'Oficina coworking en C/ Caballero de Rodas, 43',
    overlayText: 'C/ Caballero de Rodas, 43, 03181 Torrevieja, Alicante',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=C.+Caballero+de+Rodas+43+03181+Torrevieja+Alicante',
    location: 'C/ Caballero de Rodas, 43',
    detailSlug: 'coworking-caballero-43',
  },
  {
    id: 5,
    src: 'https://res.cloudinary.com/dgggdig3s/image/upload/f_auto,q_auto/AGAMED_43-1-4_xx1fvi.jpg',
    alt: 'Oficina coworking en C/ Caballero de Rodas, 43',
    overlayText: 'C/ Caballero de Rodas, 43, 03181 Torrevieja, Alicante',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=C.+Caballero+de+Rodas+43+03181+Torrevieja+Alicante',
    location: 'C/ Caballero de Rodas, 43',
    detailSlug: 'coworking-caballero-43',
  },
  {
    id: 6,
    src: 'https://res.cloudinary.com/dgggdig3s/image/upload/f_auto,q_auto/AGAMED_43-1-5_an4z9e.jpg',
    alt: 'Oficina coworking en C/ Caballero de Rodas, 43',
    overlayText: 'C/ Caballero de Rodas, 43, 03181 Torrevieja, Alicante',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=C.+Caballero+de+Rodas+43+03181+Torrevieja+Alicante',
    location: 'C/ Caballero de Rodas, 43',
    detailSlug: 'coworking-caballero-43',
  },
  {
    id: 7,
    src: 'https://res.cloudinary.com/dgggdig3s/image/upload/f_auto,q_auto/AGAMED_43-1-6_vihk1p.jpg',
    alt: 'Oficina coworking en C/ Caballero de Rodas, 43',
    overlayText: 'C/ Caballero de Rodas, 43, 03181 Torrevieja, Alicante',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=C.+Caballero+de+Rodas+43+03181+Torrevieja+Alicante',
    location: 'C/ Caballero de Rodas, 43',
    detailSlug: 'coworking-caballero-43',
  },
  {
    id: 8,
    src: 'https://res.cloudinary.com/dgggdig3s/image/upload/f_auto,q_auto/AGAMED_43-1-7_xf1piq.jpg',
    alt: 'Oficina coworking en C/ Caballero de Rodas, 43',
    overlayText: 'C/ Caballero de Rodas, 43, 03181 Torrevieja, Alicante',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=C.+Caballero+de+Rodas+43+03181+Torrevieja+Alicante',
    location: 'C/ Caballero de Rodas, 43',
    detailSlug: 'coworking-caballero-43',
  },
  {
    id: 9,
    src: 'https://res.cloudinary.com/dgggdig3s/image/upload/f_auto,q_auto/AGAMED_43-1-8_tnwnwh.jpg',
    alt: 'Oficina coworking en C/ Caballero de Rodas, 43',
    overlayText: 'C/ Caballero de Rodas, 43, 03181 Torrevieja, Alicante',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=C.+Caballero+de+Rodas+43+03181+Torrevieja+Alicante',
    location: 'C/ Caballero de Rodas, 43',
    detailSlug: 'coworking-caballero-43',
  },
  {
    id: 10,
    src: 'https://res.cloudinary.com/dgggdig3s/image/upload/f_auto,q_auto/AGAMED_43-1-9_eupvne.jpg',
    alt: 'Oficina coworking en C/ Caballero de Rodas, 43',
    overlayText: 'C/ Caballero de Rodas, 43, 03181 Torrevieja, Alicante',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=C.+Caballero+de+Rodas+43+03181+Torrevieja+Alicante',
    location: 'C/ Caballero de Rodas, 43',
    detailSlug: 'coworking-caballero-43',
  },
  // --- Oficinas en C/ Ejemplo, 4 ---
  {
    id: 11,
    src: '/fondo-hero.png',
    alt: 'Oficina coworking en C/ Ejemplo, 4',
    overlayText: 'C/ Ejemplo, 4, 03181 Torrevieja, Alicante',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=C/+Ejemplo+4+03181+Torrevieja+Alicante',
    location: 'C/ Ejemplo, 4',
    detailSlug: 'coworking-ejemplo-4',
    features: ['WiFi de alta velocidad', 'Terraza', 'Aire acondicionado', 'Parking', 'Cocina equipada'],
  },
];

/* Exportar los datos para que la página de detalle pueda usarlos */
export { LOCALES_IMAGES, COWORKING_IMAGES };

/*
 * Convierte las listas de IMÁGENES en una lista de ESPACIOS: agrupa por
 * "detailSlug" (todas las fotos del mismo sitio son un único espacio),
 * hereda los "features" de la primera foto que los declare y añade el
 * número de fotos disponibles. Es lo que consumen las tarjetas.
 */
export function getEspacios() {
  const build = (images, isCoworking) => {
    const bySlug = new Map();
    images.forEach((img) => {
      if (!img.detailSlug) return;
      if (!bySlug.has(img.detailSlug)) {
        bySlug.set(img.detailSlug, { ...img, isCoworking, photoCount: 0, features: [] });
      }
      const espacio = bySlug.get(img.detailSlug);
      espacio.photoCount += 1;
      if (img.features?.length && espacio.features.length === 0) {
        espacio.features = img.features;
      }
    });
    return [...bySlug.values()];
  };

  return [...build(LOCALES_IMAGES, false), ...build(COWORKING_IMAGES, true)];
}

const TABS = [
  { id: 'all', labelKey: 'locales.tabAll' },
  { id: 'local', labelKey: 'locales.localesTitle' },
  { id: 'coworking', labelKey: 'locales.coworkingTitle' },
];

function Locales() {
  const { t } = useLanguage();
  const [tab, setTab] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');

  const espacios = useMemo(() => getEspacios(), []);

  /* Espacios que quedan tras aplicar el tipo (pestaña) */
  const byType = useMemo(() => {
    if (tab === 'all') return espacios;
    return espacios.filter((e) => (tab === 'coworking' ? e.isCoworking : !e.isCoworking));
  }, [espacios, tab]);

  /* Las ubicaciones del desplegable se calculan sobre la pestaña activa,
   * así nunca se ofrece un filtro que dejaría la rejilla vacía. */
  const locations = useMemo(
    () => [...new Set(byType.map((e) => e.location))],
    [byType]
  );

  const visibles = useMemo(() => {
    if (selectedLocation === 'all') return byType;
    return byType.filter((e) => e.location === selectedLocation);
  }, [byType, selectedLocation]);

  const changeTab = (id) => {
    setTab(id);
    setSelectedLocation('all'); // el filtro anterior puede no existir en la nueva pestaña
  };

  return (
    <section className="section section--locales">
      <div className="section-inner">
        <h1 className="section-title">{t('locales.title')}</h1>
        <p className="section-intro">
          {t('locales.intro')}
        </p>

        <div className="espacios-toolbar">
          <div className="tabs" role="tablist" aria-label={t('locales.title')}>
            {TABS.map((tb) => (
              <button
                key={tb.id}
                type="button"
                role="tab"
                aria-selected={tab === tb.id}
                className={`tab ${tab === tb.id ? 'tab--active' : ''}`}
                onClick={() => changeTab(tb.id)}
              >
                {t(tb.labelKey)}
              </button>
            ))}
          </div>

          {locations.length > 1 && (
            <div className="espacios-filter">
              <label htmlFor="filtro-ubicacion" className="espacios-filter-label">
                {t('carousel.filterBy')}
              </label>
              <select
                id="filtro-ubicacion"
                className="coworking-filter-select"
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
              >
                <option value="all">{t('carousel.allLocations')}</option>
                {locations.map((loc) => (
                  <option key={loc} value={loc}>{loc}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <p className="espacios-count">
          {visibles.length} {visibles.length === 1 ? t('locales.countOne') : t('locales.countMany')}
        </p>

        {visibles.length > 0 ? (
          <div className="espacios-grid">
            {visibles.map((espacio) => (
              <EspacioCard key={espacio.detailSlug} espacio={espacio} chipCount={3} />
            ))}
          </div>
        ) : (
          <p className="espacios-empty">{t('locales.empty')}</p>
        )}
      </div>
    </section>
  );
}

export default Locales;
